<template>
    <bottom-sheet>
        <template #title>
            <span>Sell <asset-symbol :asset="currentAsset" /></span>
            <span class="sheet-title-price">${{ formatPrice(checkPrice) }}</span>
            <span v-if="currentAsset.price && checkPrice !== currentAsset.price" class="sheet-title-sub">
                {{ getDistance(currentAsset.price, checkPrice).formatted }}
            </span>
        </template>

        <div v-if="currentAsset.meta && currentAsset.meta.oneLiner" class="sheet-oneliner">{{ currentAsset.meta.oneLiner }}</div>

        <template v-if="positions.length">
            <div class="sheet-section-label">Positions</div>
            <compact-position-list compact :positionList="positions" :refPrice="checkPrice" />
        </template>

        <div v-if="!gotResponse" class="sheet-muted-row">
            <b-spinner small label="loading..." type="grow" variant="light" />
        </div>

        <div v-if="emptyResponse" class="sheet-muted-row">{{ emptyResponse }}</div>

        <div v-if="haveAlternativeOptions" class="sheet-notice sheet-notice-option exit-alternative">
            <span class="exit-alternative-tag">↑ {{ Math.round((alternativePriceMultiplier - 1) * 100) }}%</span>
            <span class="exit-alternative-value mono">
                {{ formatAmount(noSellableAlternativePositions * defaultPositionSize) }} @ {{ formatPrice(highestExitPrice) }}
            </span>
            <b-spinner v-if="loadingAlternatives" small type="grow" variant="light" />
            <button type="button" class="exit-alternative-check" @click="reprocessModal(formatNumber(highestExitPrice))">Check</button>
        </div>

        <template v-if="canSell" #actions>
            <div class="exit-split">
                <button
                    ref="sellButton"
                    type="button"
                    class="exit-split-sell"
                    :disabled="exitButtonBusy"
                    @click.prevent="processOutcome(selected, 'exit')"
                >
                    <b-spinner v-if="exitButtonBusy" small />
                    {{ sellLabel }}
                </button>
                <span class="exit-split-more">
                    <b-icon icon="chevron-down" />
                    <select v-model="selected">
                        <option v-for="option in exitOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                </span>
            </div>
        </template>
        <template v-else-if="canSell === false && positions.length" #actions>
            <button
                ref="sellButton"
                type="button"
                class="sheet-btn sheet-btn-warning"
                :disabled="reinvestButtonBusy"
                @click.prevent="processOutcome(positions.length, 'reinvest')"
            >
                <b-spinner v-if="reinvestButtonBusy" small />
                Reinvest position{{ ((positions.length === 1) ? '' : 's') }}
            </button>
        </template>
    </bottom-sheet>
</template>

<script>
import { mapState, mapActions } from "vuex";

import apiMixin from '@/mixins/api';
import formatterMixin from '@/mixins/formatter';
import AssetSymbol from '@/components/style/AssetSymbol.vue';
import CompactPositionList from '@/components/asset/partials/CompactPositionList.vue';
import BottomSheet from '@/components/sheet/BottomSheet.vue';

import { SIMULATED_CLOSE_RATIO, ALTERNATIVE_PRICE_STEP } from '@/constants/trading';

export default {
    name: "ProcessExit",
    props: ['targetPrice', 'currentAsset', 'positionList'],
    mixins: [
        apiMixin,
        formatterMixin
    ],
    components: {
        AssetSymbol,
        BottomSheet,
        CompactPositionList,
    },
    data() {
        return {
            emptyResponse: null,

            dataSource: 'api',
            checkPrice: 0,
            positions: [],
            noPositionsItm: 0,
            maxSellablePositions: 0,

            selected: null,
            noSellablePositions: 0,
            exitOptions: [],

            alternativePriceMultiplier: ALTERNATIVE_PRICE_STEP,
            alternativePositions: [],

            suggestedPrice: null,
            suggestedPositions: 0,

            reinvestButtonBusy: false,
            exitButtonBusy: false,

            gotResponse: false,
        }
    },
    async created() {
        if (this.positionList?.length) {
            this.positions = this.positionList;
            this.dataSource = 'props';
            this.processExitOptions();
        }

        if (this.targetPrice) this.checkPrice = this.targetPrice;
        else this.checkPrice = this.currentAsset.price;

        await this.checkExitOptions();
        if (this.dataSource !== 'api') {
            await this.checkExitByPrice(); // how stuff is at +1%
        }
    },
    methods: {
        ...mapActions([
            'removePositionsFromActionable',
            'registerPositionList',
            'updateActionableCount',
        ]),
        checkExitByPriceEndpoint(price) {
            return `/api/asset/${this.currentAsset.symbol}/check_exits/${price}`;
        },
        async checkExitByPrice() {
            const response = await this.apiGet(
                this.checkExitByPriceEndpoint(this.alternativePrice), 
                {},
                { requestId: 'checkExitByPrice' }
            );

            if (!response?.data) return;

            this.alternativePositions = response.data.positions;
        },
        async checkExitOptions() {
            const response = await this.apiGet(
                this.checkExitByPriceEndpoint(this.checkPrice), 
                {},
                { requestId: 'checkExitOptions' }
            );

            if (!response?.data) return;

            this.$store.dispatch('updateAssets', [response.data.asset]);

            this.maxSellablePositions = response.data.no_positions_itm >= 3 ? Math.floor((response.data.no_positions_itm - 1) / 2) : 0;
            this.positions = response.data.positions;
            this.noPositionsItm = response.data.no_positions_itm;

            if (!this.positions.length) this.emptyResponse = 'Nothing to process at ' + this.formatPrice(response.data.target_price);
            else this.emptyResponse = null;

            // also resets the exit options when the response is empty
            this.processExitOptions();

            this.gotResponse = true;

            // The API's in-the-money test is strict, so a suggested price that landed
            // exactly on the rounding grid drops its own boundary position.
            if (this.checkPrice !== this.suggestedPrice) return;
            if (this.noSellablePositions >= this.suggestedPositions) return;

            // no re-arming, so the price stops matching and this cannot fire twice
            this.checkPrice = this.formatNumber(this.checkPrice + this.priceStep(this.checkPrice));
            await this.checkExitOptions();
        },
        processExitOptions() {
            // Reset the exit options pool when reloading the data in the modal (like from alternative poistions)
            this.exitOptions = [];

            let sumClosed = 0;
            let posExit = 0;

            this.positions.forEach((pos, idx) => {
                if (idx % 2 === 0 && posExit < this.maxSellablePositions) {
                    posExit++;
                    sumClosed += pos.close_price;

                    this.exitOptions.push({
                        value: posExit,
                        text: `${this.formatAmount(this.defaultPositionSize * posExit)} @ ${this.formatPrice(sumClosed / posExit)}`
                    });
                }
            });

            this.selected = posExit;
            this.noSellablePositions = posExit;
        },
        async processOutcome(noPositions, outcome) {
            if (this.exitButtonBusy || this.reinvestButtonBusy) return;

            if (outcome === 'reinvest') this.reinvestButtonBusy = true;
            else this.exitButtonBusy = true;

            const response = await this.apiPost(
                '/api/positions/process-positions/',
                {
                    type: outcome,
                    no_positions: noPositions,
                    asset_symbol: this.currentAsset.symbol,
                    target_price: this.checkPrice
                },
                { requestId: 'processOutcome', showSuccessToast: true }
            );

            this.exitButtonBusy = false;
            this.reinvestButtonBusy = false;

            if (!response) return;

            this.$store.dispatch('updateAssets', [response.data.asset]);

            let exitPids = []
            let reinvestedPids = []
            let newPositions = []
            if ('exit_pids' in response.data) exitPids = response.data.exit_pids;
            if ('reinvested_pids' in response.data) reinvestedPids = response.data.reinvested_pids;
            if ('new_positions' in response.data) newPositions = response.data.new_positions;

            let closedPids = [...exitPids, ...reinvestedPids];
            this.removePositionsFromActionable(closedPids);
            this.registerPositionList(newPositions);

            // reinvests reopen still-actionable positions, so the count can't be
            // derived from the closed pids — take the server's
            if ('actionable_count' in response.data) {
                this.updateActionableCount({
                    ticker: this.currentAsset.symbol,
                    count: response.data.actionable_count,
                });
            }

            // Update last events list
            this.$store.dispatch('retrieveLastOpenPositions');

            this.$FModal.hide();
        },
        reprocessModal(checkPrice) {
            this.suggestedPrice = checkPrice;
            this.suggestedPositions = this.noSellableAlternativePositions;
            this.checkPrice = checkPrice;
            this.checkExitOptions();
        },
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        alternativePrice() {
            return this.formatNumber(this.checkPrice * this.alternativePriceMultiplier);
        },
        loadingAlternatives() {
            return !!this.apiLoading['checkExitByPrice'];
        },
        noSellableAlternativePositions() {
            return Math.floor(this.alternativePositions.length / 2);
        },
        highestExitPrice() {
            // get the highest price from the alternative positions
            const sortedAlternatives = [...this.alternativePositions].sort((a, b) => b.open_price - a.open_price);
            
            if (sortedAlternatives.length % 2 === 0) {
                // if the length is even, remove the first elemen
                // the last position will be reinvested regardless
                sortedAlternatives.shift();
            }

            return sortedAlternatives[0].open_price * SIMULATED_CLOSE_RATIO;
        },
        haveAlternativeOptions() {
            if (
                this.dataSource === 'api' ||
                !this.canSell ||
                this.noSellableAlternativePositions <= this.noSellablePositions
            ) {
                return false;
            }

            // show options only if there's a valid alternative and response was not sourced from an API query
            // avoid adding the multiplier over and over again
            return true;
        },
        sellLabel() {
            const option = this.exitOptions.find(option => option.value === this.selected);
            return option ? `Sell ${option.text}` : 'Sell';
        },
        canSell() {
            if (!this.gotResponse) return null;

            if (!this.positions.length || !this.maxSellablePositions) return false;

            return true;
        }
    }
};
</script>

<style scoped>
.exit-alternative {
    display: flex;
    align-items: center;
    gap: 10px;
}
.exit-alternative-tag {
    flex: none;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(23, 162, 184, .18);
    color: var(--sheet-link);
    font-size: 11px;
    font-weight: 700;
}
.exit-alternative-value {
    flex: 1;
    white-space: nowrap;
}
.exit-alternative-check {
    flex: none;
    padding: 4px 10px;
    border: 1px solid var(--sheet-link);
    border-radius: 6px;
    background: none;
    color: var(--sheet-link);
    font-size: 13px;
    font-weight: 600;
}
.exit-split {
    display: flex;
    height: 50px;
    border-radius: 10px;
    overflow: hidden;
    background: #28a745;
}
.exit-split-sell {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 0;
    background: none;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
}
.exit-split-sell:disabled {
    opacity: .6;
}
/* wide enough that a tap meant for the amounts does not land on Sell */
.exit-split-more {
    position: relative;
    flex: none;
    width: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid rgba(0, 0, 0, .3);
    background: #1f8a37;
    color: #fff;
}
.exit-split-more select {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    font-size: 16px;
}
</style>
